import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, RotateCcw } from "lucide-react";
import { ALLOWED_FIELD_KEYS } from "@avestate/shared";
import { useApprovals, useCreateApproval } from "@/hooks/useApprovals.js";
import { useAISuggestions } from "@/hooks/useAISuggestions.js";
import { Button } from "@/components/ui/button.js";
import { Textarea } from "@/components/ui/textarea.js";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "@/components/ui/table.js";
import { Badge } from "@/components/ui/badge.js";
import { RoleGate } from "@/components/RoleGate.js";
import { ListingChrome } from "@/components/signature/ListingChrome.js";

const DECISION_TONE: Record<"approved" | "rejected" | "needs_revision", "success" | "risk" | "warn"> = {
  approved: "success",
  rejected: "risk",
  needs_revision: "warn",
};

export default function ListingApprovals(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  if (!id) return <p className="text-risk">id mancante</p>;

  const { data: approvalsRes } = useApprovals(id);
  const { data: suggestions = [] } = useAISuggestions(id);
  const create = useCreateApproval(id);

  const [claim, setClaim] = useState<string>(ALLOWED_FIELD_KEYS[0]);
  const [comment, setComment] = useState<string>("");

  async function decide(decision: "approved" | "rejected" | "needs_revision") {
    try {
      await create.mutateAsync({ claim_key: claim as never, decision, comment: comment || undefined });
      toast.success(`Decisione registrata: ${decision}`);
      setComment("");
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const approvals = approvalsRes?.approvals ?? [];

  return (
    <div className="space-y-6">
      {id ? <ListingChrome id={id} /> : null}
      <header>
        <p className="font-mono text-meta uppercase tracking-wider text-ink-mute">
          <Link to={`/listings/${id}`} className="hover:text-ink">↶ editor</Link>
        </p>
        <h1 className="mt-1 text-h1 font-display text-ink">Approvazioni</h1>
        <p className="text-body text-ink-soft">
          {suggestions.length} suggerimenti in attesa · {approvals.length} decisioni registrate
        </p>
      </header>

      <RoleGate min="reviewer">
        <section className="rounded border border-rule bg-surface p-5">
          <h2 className="mb-4 text-h2 font-display text-ink">Nuova decisione</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[200px_1fr]">
            <select
              value={claim}
              onChange={(e) => setClaim(e.target.value)}
              className="h-9 rounded-sm border border-rule bg-surface-2 px-3 text-body text-ink"
              aria-label="Campo claim"
            >
              {ALLOWED_FIELD_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Commento opzionale (es. APE verificata su certificato)"
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => void decide("approved")} disabled={create.isPending}>
              <Check className="size-3.5" /> Approva
            </Button>
            <Button variant="outline" onClick={() => void decide("needs_revision")} disabled={create.isPending}>
              <RotateCcw className="size-3.5" /> Richiedi revisione
            </Button>
            <Button variant="danger" onClick={() => void decide("rejected")} disabled={create.isPending}>
              <X className="size-3.5" /> Rifiuta
            </Button>
          </div>
        </section>
      </RoleGate>

      <div className="overflow-x-auto rounded border border-rule bg-surface">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-[200px]">Data</TableHeader>
              <TableHeader className="w-[180px]">Claim</TableHeader>
              <TableHeader className="w-[140px]">Decisione</TableHeader>
              <TableHeader>Commento</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {approvals.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-mono text-meta text-ink">{new Date(a.decided_at).toLocaleString("it-IT")}</TableCell>
                <TableCell className="font-mono text-meta uppercase text-ink-soft">{a.claim_key}</TableCell>
                <TableCell><Badge tone={DECISION_TONE[a.decision]}>{a.decision}</Badge></TableCell>
                <TableCell className="text-ink-soft">{a.comment ?? "—"}</TableCell>
              </TableRow>
            ))}
            {approvals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-10 text-center text-ink-mute">Nessuna decisione registrata.</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
